import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdAdd, MdEdit } from 'react-icons/md';
import {
  AppCard,
  AppInput,
  AppSelect,
  AppTextarea,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  TablePagination,
  PageLoader,
} from '../../../../components/ui';
import { ActiveBadge } from '../MasterDataPage';
import {
  EQUIPMENT_CATEGORIES,
  VEHICLE_CLASSES,
} from '../../../../data/master/equipments';
import {
  createEquipmentType,
  listEquipmentTypes,
  updateEquipmentType,
} from '../../../../repositories/equipmentType.repository';
import { usePagination } from '../../../../hooks/usePagination';
import { useTableSort } from '../../../../hooks/useTableSort';

const emptyForm = {
  name: '',
  category: 'Agriculture',
  categoryOther: '',
  vehicleClass: 'LMV',
  description: '',
  active: true,
};

export default function EquipmentsPage() {
  const toast = useToast();
  const addModal = useDisclosure();
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editingCode, setEditingCode] = useState(null);

  const loadEquipments = useCallback(
    async ({ showSpinner = true } = {}) => {
      if (showSpinner) setLoading(true);
      try {
        const result = await listEquipmentTypes({ pageSize: 500 });
        setEquipments(result.items || []);
      } catch (err) {
        toast({
          title: err?.message || 'Failed to load equipment types',
          status: 'error',
          position: 'top-right',
        });
        setEquipments([]);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    loadEquipments();
  }, [loadEquipments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const knownCategories = EQUIPMENT_CATEGORIES.filter((c) => c !== 'Other');
    return equipments.filter((e) => {
      const matchQ =
        !q ||
        (e.name || '').toLowerCase().includes(q) ||
        (e.equipmentId || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        (e.category || '').toLowerCase().includes(q);
      const matchCat =
        !categoryFilter ||
        (categoryFilter === 'Other'
          ? !knownCategories.includes(e.category)
          : e.category === categoryFilter);
      const matchClass = !classFilter || e.vehicleClass === classFilter;
      return matchQ && matchCat && matchClass;
    });
  }, [equipments, search, categoryFilter, classFilter]);

  const { sorted, sort, toggleSort } = useTableSort(filtered);

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(sorted);

  const openAdd = () => {
    setEditingId(null);
    setEditingCode(null);
    setForm(emptyForm);
    addModal.onOpen();
  };

  const openEdit = (row) => {
    const known = EQUIPMENT_CATEGORIES.includes(row.category);
    setEditingId(row.id);
    setEditingCode(row.equipmentId || null);
    setForm({
      name: row.name || '',
      category: known ? row.category : 'Other',
      categoryOther: known ? '' : row.category || '',
      vehicleClass: row.vehicleClass || 'LMV',
      description: row.description || '',
      active: row.active !== false,
    });
    addModal.onOpen();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast({
        title: 'Equipment type name is required',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    const category =
      form.category === 'Other' ? form.categoryOther.trim() : form.category;
    if (!category) {
      toast({
        title: form.category === 'Other' ? 'Enter a custom category' : 'Category is required',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    const payload = {
      name: form.name.trim(),
      category,
      vehicleClass: form.vehicleClass,
      description: form.description.trim(),
      active: form.active,
    };

    setSaving(true);
    try {
      if (editingId != null) {
        await updateEquipmentType(editingId, payload);
        toast({
          title: 'Equipment type updated',
          description: `${payload.name}${editingCode ? ` · ${editingCode}` : ''}`,
          status: 'success',
          position: 'top-right',
        });
      } else {
        const created = await createEquipmentType(payload);
        toast({
          title: 'Equipment type saved',
          description: `${payload.name}${created?.equipmentId ? ` · ${created.equipmentId}` : ''}`,
          status: 'success',
          position: 'top-right',
        });
      }
      addModal.onClose();
      await loadEquipments({ showSpinner: false });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save equipment type',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader label="Loading equipment types…" />;
  }

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <Heading size="lg">Equipments Master</Heading>
          <Text color="text.secondary" mt={2} maxW="640px">
            Master equipment / vehicle types used when villagers list assets.
          </Text>
        </Box>
        <PrimaryButton size="sm" leftIcon={<MdAdd />} onClick={openAdd}>
          Add equipment type
        </PrimaryButton>
      </Flex>

      <AppCard mb={4}>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3} alignItems="end">
          <Box>
            <FormLabel fontSize="13px" mb={2}>
              Search
            </FormLabel>
            <Input
              h="40px"
              fontSize="14px"
              placeholder="Name / ID / description"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <AppSelect
            label="Category"
            labelFontSize="13px"
            h="40px"
            placeholder="All categories"
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v || '')}
            options={[
              { value: '', label: 'All categories' },
              ...EQUIPMENT_CATEGORIES.map((c) => ({ value: c, label: c })),
            ]}
          />
          <AppSelect
            label="Vehicle class"
            labelFontSize="13px"
            h="40px"
            placeholder="All classes"
            value={classFilter}
            onChange={(v) => setClassFilter(v || '')}
            options={[
              { value: '', label: 'All classes' },
              ...VEHICLE_CLASSES.map((c) => ({ value: c, label: c })),
            ]}
          />
          <OutlineButton
            size="sm"
            w="100%"
            h="40px"
            minH="40px"
            onClick={() => {
              setSearch('');
              setCategoryFilter('');
              setClassFilter('');
            }}
          >
            Clear filters
          </OutlineButton>
        </SimpleGrid>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
          <Text fontSize="13px" color="text.secondary">
            {totalCount} equipment type{totalCount === 1 ? '' : 's'}
          </Text>
        </Box>
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                <SortableTh columnKey="equipmentId" sort={sort} onSort={toggleSort}>
                  ID
                </SortableTh>
                <SortableTh columnKey="name" sort={sort} onSort={toggleSort}>
                  Equipment type
                </SortableTh>
                <SortableTh columnKey="category" sort={sort} onSort={toggleSort}>
                  Category
                </SortableTh>
                <SortableTh columnKey="vehicleClass" sort={sort} onSort={toggleSort}>
                  Vehicle class
                </SortableTh>
                <SortableTh columnKey="active" sort={sort} onSort={toggleSort}>
                  Status
                </SortableTh>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pageItems.map((row) => (
                <Tr key={row.id ?? row.equipmentId}>
                  <Td fontFamily="mono" fontSize="13px">
                    {row.equipmentId}
                  </Td>
                  <Td fontWeight="600">{row.name}</Td>
                  <Td>{row.category}</Td>
                  <Td>{row.vehicleClass}</Td>
                  <Td>
                    <ActiveBadge active={row.active} />
                  </Td>
                  <Td>
                    <IconButton
                      aria-label={`Edit ${row.name}`}
                      icon={<MdEdit />}
                      size="sm"
                      variant="ghostInk"
                      onClick={() => openEdit(row)}
                    />
                  </Td>
                </Tr>
              ))}
              {pageItems.length === 0 ? (
                <Tr>
                  <Td colSpan={6}>
                    <Text color="text.secondary" py={6} textAlign="center">
                      No equipment types match your filters.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
        <TablePagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </AppCard>

      <Modal
        isOpen={addModal.isOpen}
        onClose={() => {
          if (!saving) addModal.onClose();
        }}
        size="xl"
        isCentered
        scrollBehavior="inside"
        blockScrollOnMount
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          as="form"
          onSubmit={handleSave}
          bg="bg.surface"
          borderRadius="md"
          mx={4}
          my={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader flexShrink={0}>
            {editingId != null
              ? `Edit equipment type · ${editingCode || editingId}`
              : 'Add equipment type'}
          </ModalHeader>
          <ModalCloseButton isDisabled={saving} />
          <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
            <VStack align="stretch" spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <AppInput
                  label="Equipment type name"
                  isRequired
                  placeholder="e.g. Tractor"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
                <AppSelect
                  label="Category"
                  isRequired
                  value={form.category}
                  onChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      category: v,
                      categoryOther: v === 'Other' ? f.categoryOther : '',
                    }))
                  }
                  options={EQUIPMENT_CATEGORIES.map((c) => ({ value: c, label: c }))}
                  helperText={
                    form.category === 'Other'
                      ? 'Choose Other to type your own category.'
                      : undefined
                  }
                />
                {form.category === 'Other' ? (
                  <AppInput
                    label="Custom category"
                    isRequired
                    placeholder="Type category name"
                    value={form.categoryOther}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, categoryOther: e.target.value }))
                    }
                  />
                ) : null}
                <AppSelect
                  label="Vehicle class"
                  isRequired
                  value={form.vehicleClass}
                  onChange={(v) => setForm((f) => ({ ...f, vehicleClass: v }))}
                  options={VEHICLE_CLASSES.map((c) => ({ value: c, label: c }))}
                  helperText="LMV / HMV / Construction Equipment"
                />
              </SimpleGrid>
              <AppTextarea
                label="Description"
                placeholder="Optional notes for admins"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
              />
              <HStack justify="space-between">
                <FormLabel mb={0}>Active</FormLabel>
                <Switch
                  isChecked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  colorScheme="yellow"
                />
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter
            flexShrink={0}
            gap={2}
            borderTopWidth="1px"
            borderColor="border.subtle"
            bg="bg.surface"
            zIndex={2}
          >
            <OutlineButton type="button" onClick={addModal.onClose} isDisabled={saving}>
              Cancel
            </OutlineButton>
            <PrimaryButton type="submit" isLoading={saving}>
              Save
            </PrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
